import WebFont from 'webfontloader';
import getStore from 'services/getStore';
import fonts from './fonts';
import parseFontUrl from './parseFontUrl';
import { loadAssets } from 'components/lib/BootScreen';
import pubSub from 'services/pubSub';
import { TButton } from 'store/store';

const store = getStore();
const { getState } = store;
const { fontFamily = '', fontFamilies = [], buttons = {} } = getState().theme;

const fontsFromThemeButtons = (Object.values(buttons) as TButton[]).map(
  button => {
    return button?.fontFamily;
  }
);

const getCustomFonts = (): {}[] => {
  return getState().customFonts;
};

type F = {
  name: string;
};

const getCustomFontNames = (customFonts: {}[] = []): string[] => {
  return customFonts.map(f => (f as F)?.name);
};

export const LOAD_FONT_ON_DEMAND = 'loadFontOnDemand';

// fontFamilyMarks are any spans moving through slate
// we can refactor this to something faster later maybe
let fontFamilyMarks: string[] = [];

// any other reference to font or fontFamily that exists on a block's props
// indicates it is/was editable. capture it
let anyFontOrFontFamilyPropOnABlock: string[] = [];

Object.values(store.getState().blocks).forEach((b: any) => {
  const font = b?.props?.font || b?.props?.fontFamily;
  if (font) {
    anyFontOrFontFamilyPropOnABlock.push(font);
  }

  if (b.type === 'Text' && b?.props?.content) {
    const fontFamilyMark = b.props.content
      .split(/\<|\>/g)
      .filter((entry: any) => entry.includes('style="font-family:'))
      .map((entry: any) =>
        entry
          .replace(/\"|span|style|\=|font-family:|data-type|fontFamily/g, '')
          .trimStart()
          .trimEnd()
      )
      .filter((entry: any) => entry !== '');

    fontFamilyMarks = [...fontFamilyMarks, ...fontFamilyMark];
  }
});

// fontFamily is the font added to body
const userSelectedFonts = Array.from(
  new Set([
    fontFamily,
    ...fontFamilyMarks,
    ...anyFontOrFontFamilyPropOnABlock,
    ...fontsFromThemeButtons,
    // ...customFonts.map(cF => cF.name),
  ]) as any
).filter(f => {
  // filter out anything that might've returned an empty string ''
  return f;
}) as string[];

// TODO
// turn this into a promise and move it out into own file ? . . .
const addFont = (url, targetWindow = window) => {
  if (!url) {
    return;
  }

  const { fontName, fontExtension, fontFormat } = parseFontUrl(url);

  // if it's default ( empty ) or is on the google fonts list ( fonts ). bail
  if (!fontName || fonts.includes(fontName)) {
    return fontName;
  }

  const head = targetWindow.document.querySelector('head')!;
  if (head.querySelector(`#${fontName}`)) {
    return;
  }

  const styleTag = document.createElement('style');

  styleTag.id = fontName;
  styleTag.innerHTML = `
    @font-face {
      font-family: "${fontName}";
      src: url("${url}") format("${fontFormat}");
    }
  `;

  head.appendChild(styleTag);

  return fontName;
};

export type ILoadFontOnDemand = {
  font: string;
  onFontLoaded: (font: string) => void;
};

pubSub.subscribe(LOAD_FONT_ON_DEMAND, ($: ILoadFontOnDemand) => {
  const { font = '', onFontLoaded = () => {} } = $;

  let editor;
  let module;

  if (window.IS_CHAMELEON_EDITOR) {
    editor = window;
    module = editor.frames[0];
  } else {
    editor = window.parent;
    module = window;
  }

  if (!font) {
    onFontLoaded(font);
    return;
  }

  const customFontNames = getCustomFontNames(getCustomFonts());
  const fontIsCustomFont = customFontNames.includes(font);

  //
  // no need to use the WebFont.loader if we don't want
  // was only in play to have a consistent api but since the custom fonts
  // are all loaded ahead of time. . .
  if (fontIsCustomFont) {
    console.log(`loading custom font : ${font}`);
    onFontLoaded(font);
    return;
  }

  console.log(`loading google font : ${font}`);

  [editor, module].forEach(w => {
    WebFont.load({
      ...(!fontIsCustomFont && {
        google: {
          families: [`${font}:400,700`],
        },
      }),
      loading: () => {},
      active: () => {
        // console.log('font is loading ?', font);
        onFontLoaded(font);
      },
      inactive: () => {
        alert(
          'A network error occurred and your font could not be loaded. Please try again.'
        );
      },
      fontloading: f => {
        // console.log('font is loading ? :', f);
      },
      context: w,
    });
  });
});

const WebFontConfig = {
  google: {
    families: userSelectedFonts
      .filter(f => {
        //
        // console.log(f);
        return !getCustomFontNames(getCustomFonts()).includes(f);
      })
      .map(f => `${f}:400,700`),
  },
  loading: () => {},
  active: () => {
    loadAssets.loadFonts && loadAssets.loadFonts();
  },
  inactive: () => {
    console.log('NO UserSelectedFonts TO LOAD');
    loadAssets.loadFonts && loadAssets.loadFonts();
  },
  fontloading: (familyName, fvd) => {
    // console.log(familyName, fvd);
  },
  fontactive: (familyName, fvd) => {
    // console.log(familyName, fvd);
  },
  fontinactive: (familyName, fvd) => {
    // console.log(familyName, fvd);
  },
};

// boot these up. if any
if (!!fontFamilies.length) {
  fontFamilies.forEach(f => {
    addFont(f);
  });
}

console.log({
  window: window.location.href,
  userSelectedFonts,
  'WebFontConfig.google.families': WebFontConfig.google.families,
});

if (WebFontConfig.google.families.length) {
  WebFont.load(WebFontConfig);
}

export { addFont };
